"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { BooruPost } from "./booru-api"
import { mediaItemsFromBooruPost } from "./order-media"

// "grid-custom" is additive, not a replacement for the three fixed presets —
// those keep their exact existing behavior/Tailwind classes untouched, so
// nothing about them can regress. Custom mode is its own fifth value with
// its own cell-count/column state below, rendered via inline grid-template
// styles instead of Tailwind's grid-cols-N utilities (which can't take a
// dynamic N — Tailwind needs the literal class string present in source to
// generate it, so "grid-cols-" + n is invisible to it at build time).
export type GooncaveLayout = "single" | "grid-2x2" | "grid-2x3" | "grid-2x4" | "grid-custom"
export type GooncaveSource = "favorites" | "current-search" | "custom"
export type GridDisplayMode = "cover" | "contain" | "fill" | "masonry"

interface GooncaveState {
  // Queue
  queue: BooruPost[]
  currentIndex: number

  // Playback settings
  layout: GooncaveLayout
  // Only meaningful when layout === "grid-custom". Hard-capped 1-12 (spec
  // decision #4: warn at 8+, never allow past 12 — the ceiling is real
  // browsers' simultaneous-video-decoder limit, not an arbitrary UI choice).
  customCellCount: number
  // "auto" picks column count from viewport width (phone 2 / tablet 3 /
  // desktop 4+, see hooks/use-gooncave-auto-columns.ts) so the default
  // behaves reasonably with zero configuration. A number pins an exact
  // column count regardless of viewport.
  customCols: number | "auto"
  isPlaying: boolean
  slideshowInterval: number // seconds
  shuffleQueue: boolean
  loopQueue: boolean
  expandAlbums: boolean
  autoLoadToQueue: boolean
  gridDisplayMode: GridDisplayMode
  autoPlayVideos: boolean
  autoPlayGifs: boolean

  // Source
  source: GooncaveSource

  // Grid state
  activeCells: number[]

  // Video sync state
  videoEndedCells: Set<number>
  maxVideoDuration: number
  repeatCurrentSet: boolean
  videoDurations: Map<number, number>

  // Cell-specific settings
  cellSpeeds: Map<number, number>
  cellVolumes: Map<number, number>
  mutedCells: Set<number>
  // Per-cell loop override — distinct from repeatCurrentSet (a single GLOBAL
  // toggle duplicated onto every cell's toolbar for convenience, same as
  // isPlaying) and from loopQueue (cycles the whole queue). This lets one
  // specific cell repeat its video regardless of what the global toggle is
  // set to, independent of every other cell.
  loopingCells: Set<number>

  // Actions
  setQueue: (posts: BooruPost[]) => void
  addToQueue: (post: BooruPost | BooruPost[]) => void
  removeFromQueue: (index: number) => void
  clearQueue: () => void
  setLayout: (layout: GooncaveLayout) => void
  setCustomCellCount: (count: number) => void
  setCustomCols: (cols: number | "auto") => void
  setIsPlaying: (playing: boolean) => void
  setSlideshowInterval: (seconds: number) => void
  setShuffleQueue: (shuffle: boolean) => void
  setLoopQueue: (loop: boolean) => void
  setExpandAlbums: (expand: boolean) => void
  setSource: (source: GooncaveSource) => void
  setAutoLoadToQueue: (autoLoad: boolean) => void
  setGridDisplayMode: (mode: GridDisplayMode) => void
  setAutoPlayVideos: (v: boolean) => void
  setAutoPlayGifs: (v: boolean) => void
  reorderQueue: (fromIndex: number, toIndex: number) => void
  replaceGridCell: (cellIndex: number, queueIndex: number) => void
  // Points a grid cell straight at a queue index with no swap-back — unlike
  // replaceGridCell (built for dragging one filled cell's content onto another), this is for
  // filling a cell that's empty, where there's no "old item" to preserve.
  setCellContent: (cellIndex: number, queueIndex: number) => void
  nextSlide: () => void
  prevSlide: () => void
  goToSlide: (index: number) => void

  // Video sync
  setVideoEnded: (cellIndex: number, ended: boolean) => void
  setVideoDuration: (cellIndex: number, duration: number) => void
  setRepeatCurrentSet: (repeat: boolean) => void
  advanceAllCells: () => void
  checkAllVideosEnded: () => boolean

  // Cell controls
  setCellSpeed: (cellIndex: number, speed: number) => void
  resetCellSpeed: (cellIndex: number) => void
  setCellVolume: (cellIndex: number, volume: number) => void
  toggleMuteCell: (cellIndex: number) => void
  muteAllCells: () => void
  unmuteAllCells: () => void
  toggleLoopCell: (cellIndex: number) => void

  // Grid helpers
  getLayoutCellCount: () => number
  initializeGridCells: () => void
  advanceCell: (cellIndex: number) => void
}

// Returns the logical start index of the current grid page, handling wrap-around.
// e.g. activeCells=[8,9,0,1] with queue.length=10 → 8 (not 0)
function findGridStart(activeCells: number[], queueLength: number): number {
  if (activeCells.length === 0) return 0
  const sorted = [...activeCells].sort((a, b) => a - b)
  let maxGap = sorted[0] + queueLength - sorted[sorted.length - 1] // wrap-around gap
  let startIdx = 0 // index in sorted[] that is the first element of the page
  for (let i = 0; i < sorted.length - 1; i++) {
    const gap = sorted[i + 1] - sorted[i]
    if (gap > maxGap) {
      maxGap = gap
      startIdx = i + 1
    }
  }
  return sorted[startIdx]
}

export const useGooncaveStore = create<GooncaveState>()(
  persist(
    (set, get) => ({
      queue: [],
      currentIndex: 0,
      layout: "single",
      customCellCount: 8,
      customCols: "auto",
      isPlaying: false,
      slideshowInterval: 5,
      shuffleQueue: false,
      loopQueue: true,
      expandAlbums: true,
      autoLoadToQueue: false,
      gridDisplayMode: "contain",
      autoPlayVideos: true,
      autoPlayGifs: true,
      source: "favorites",
      activeCells: [0],
      videoEndedCells: new Set(),
      maxVideoDuration: 0,
      repeatCurrentSet: false,
      videoDurations: new Map(),
      cellSpeeds: new Map(),
      cellVolumes: new Map(),
      mutedCells: new Set([0, 1, 2, 3, 4, 5, 6, 7]), // Default muted
      loopingCells: new Set(), // Off by default — global repeatCurrentSet/loopQueue already cover the common cases

      setQueue: (posts) => {
        set({ queue: posts, currentIndex: 0 })
        get().initializeGridCells()
      },

      addToQueue: (post) => {
        const { queue, expandAlbums } = get()
        const postsToAdd = Array.isArray(post) ? post : [post]

        const newPosts: BooruPost[] = []

        for (const p of postsToAdd) {
          const exists = queue.some((qp) => qp.id === p.id && qp.site === p.site)
          if (exists) continue

          const mediaItems = mediaItemsFromBooruPost(p)

          if (expandAlbums && mediaItems.length > 1) {
            mediaItems.forEach((item, index) => {
              newPosts.push({
                ...p,
                fileUrl: item.url,
                previewUrl: item.thumbnailUrl || p.previewUrl || p.sampleUrl || item.url,
                sampleUrl: item.type === "video" ? item.url : p.sampleUrl || item.thumbnailUrl || item.url,
                type: item.type === "video" ? "video" : "image",
                _albumId: p.id,
                _albumIndex: index,
                _albumTotal: mediaItems.length,
              } as BooruPost)
            })
          } else {
            const item = mediaItems[0]
            newPosts.push(item ? ({
              ...p,
              fileUrl: item.url,
              previewUrl: item.thumbnailUrl || p.previewUrl || p.sampleUrl || item.url,
              sampleUrl: item.type === "video" ? item.url : p.sampleUrl || item.thumbnailUrl || item.url,
              type: item.type === "video" ? "video" : "image",
            } as BooruPost) : p)
          }
        }

        if (newPosts.length > 0) {
          set({ queue: [...queue, ...newPosts] })
        }
      },

      removeFromQueue: (index) => {
        const { queue, currentIndex } = get()
        const newQueue = queue.filter((_, i) => i !== index)
        const newIndex = currentIndex >= newQueue.length ? Math.max(0, newQueue.length - 1) : currentIndex
        set({ queue: newQueue, currentIndex: newIndex })
        // activeCells holds queue indices — removing an item shifts everything after it down
        // by one and can leave cells pointing past the new end, so it has to be recomputed
        // the same way reorderQueue does, otherwise grid slots silently point at the wrong
        // (or a now out-of-range) post.
        get().initializeGridCells()
      },

      clearQueue: () =>
        set({
          queue: [],
          currentIndex: 0,
          activeCells: [0],
          videoEndedCells: new Set(),
          maxVideoDuration: 0,
          repeatCurrentSet: false,
          videoDurations: new Map(),
          cellSpeeds: new Map(),
        }),

      setLayout: (layout) => {
        set({ layout, cellSpeeds: new Map(), videoEndedCells: new Set(), videoDurations: new Map() })
        get().initializeGridCells()
      },

      setCustomCellCount: (count) => {
        // Hard cap 12 (spec decision #4) — the ceiling is real, not
        // decorative, so it's enforced here rather than trusted to the UI.
        const clamped = Math.max(1, Math.min(12, Math.round(count)))
        set({ customCellCount: clamped })
        // Only rebuild the grid if custom mode is actually active — no point
        // resetting activeCells for a count that isn't in use yet.
        if (get().layout === "grid-custom") get().initializeGridCells()
      },

      setCustomCols: (cols) => {
        set({ customCols: cols })
        // Column count doesn't change how many cells exist, only how they
        // wrap — no initializeGridCells() call needed, unlike cell count.
      },

      setIsPlaying: (playing) => set({ isPlaying: playing }),
      setSlideshowInterval: (seconds) => set({ slideshowInterval: seconds }),
      setShuffleQueue: (shuffle) => set({ shuffleQueue: shuffle }),
      setLoopQueue: (loop) => set({ loopQueue: loop }),
      setExpandAlbums: (expand) => set({ expandAlbums: expand }),
      setSource: (source) => set({ source: source }),
      setAutoLoadToQueue: (autoLoad) => set({ autoLoadToQueue: autoLoad }),
      setGridDisplayMode: (mode) => set({ gridDisplayMode: mode }),

      reorderQueue: (fromIndex, toIndex) => {
        const { queue } = get()
        if (
          fromIndex === toIndex ||
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= queue.length ||
          toIndex >= queue.length
        ) {
          return
        }

        const newQueue = [...queue]
        const [movedItem] = newQueue.splice(fromIndex, 1)
        newQueue.splice(toIndex, 0, movedItem)

        set({ queue: newQueue })
        get().initializeGridCells()
      },

      replaceGridCell: (cellIndex, queueIndex) => {
        const { activeCells, queue } = get()
        if (cellIndex < 0 || cellIndex >= activeCells.length || queueIndex < 0 || queueIndex >= queue.length) {
          return
        }

        const oldQueueIndex = activeCells[cellIndex]
        const newCells = [...activeCells]
        newCells[cellIndex] = queueIndex

        if (oldQueueIndex !== queueIndex) {
          const newQueue = [...queue]
          const [replacedItem] = newQueue.splice(oldQueueIndex, 1)
          newQueue.push(replacedItem)
          set({ queue: newQueue, activeCells: newCells })
        } else {
          set({ activeCells: newCells })
        }
      },

      setCellContent: (cellIndex, queueIndex) => {
        const { activeCells, queue } = get()
        // Bound against the LAYOUT's cell count, not activeCells.length: the two diverge
        // whenever activeCells hasn't been rebuilt for the current layout (clearQueue resets
        // it to [0], and addToQueue never rebuilds it), while the grid still renders a full
        // set of slots. Checking the array length instead made "add content to THIS slot"
        // silently no-op for every slot past the first, dropping the post into cell 0.
        const cellCount = get().getLayoutCellCount()
        if (cellIndex < 0 || cellIndex >= cellCount || queueIndex < 0 || queueIndex >= queue.length) {
          return
        }
        const newCells = [...activeCells]
        // -1 is the established "empty, show Add content" sentinel — see initializeGridCells.
        while (newCells.length < cellCount) newCells.push(-1)
        newCells[cellIndex] = queueIndex
        set({ activeCells: newCells })
      },

      nextSlide: () => {
        const { queue, currentIndex, loopQueue, shuffleQueue } = get()
        if (queue.length === 0) return

        let nextIndex: number
        if (shuffleQueue) {
          nextIndex = Math.floor(Math.random() * queue.length)
        } else {
          nextIndex = currentIndex + 1
          if (nextIndex >= queue.length) {
            nextIndex = loopQueue ? 0 : queue.length - 1
          }
        }
        set({ currentIndex: nextIndex, videoEndedCells: new Set(), videoDurations: new Map() })
      },

      prevSlide: () => {
        const { queue, currentIndex, loopQueue, layout } = get()
        if (queue.length === 0) return

        if (layout !== "single") {
          const cellCount = get().getLayoutCellCount()
          const { activeCells } = get()
          const gridStart = findGridStart(activeCells, queue.length)
          const newStart = loopQueue
            ? ((gridStart - cellCount) % queue.length + queue.length) % queue.length
            : Math.max(gridStart - cellCount, 0)
          const newCells = Array.from({ length: cellCount }, (_, i) => (newStart + i) % queue.length)
          set({ activeCells: newCells, videoEndedCells: new Set(), videoDurations: new Map() })
          return
        }

        let prevIndex = currentIndex - 1
        if (prevIndex < 0) {
          prevIndex = loopQueue ? queue.length - 1 : 0
        }
        set({ currentIndex: prevIndex, videoEndedCells: new Set(), videoDurations: new Map() })
      },

      goToSlide: (index) => {
        const { queue } = get()
        if (index >= 0 && index < queue.length) {
          set({ currentIndex: index, videoEndedCells: new Set(), videoDurations: new Map() })
        }
      },

      setVideoEnded: (cellIndex, ended) => {
        const { videoEndedCells } = get()
        const newSet = new Set(videoEndedCells)
        if (ended) {
          newSet.add(cellIndex)
        } else {
          newSet.delete(cellIndex)
        }
        set({ videoEndedCells: newSet })
      },

      setVideoDuration: (cellIndex, duration) => {
        const { videoDurations } = get()
        const newDurations = new Map(videoDurations)
        newDurations.set(cellIndex, duration)
        set({ videoDurations: newDurations })
      },

      setRepeatCurrentSet: (repeat) => set({ repeatCurrentSet: repeat }),

      checkAllVideosEnded: () => {
        const { videoEndedCells, activeCells, queue } = get()
        // Check if all video cells have ended
        let videoCount = 0
        for (let i = 0; i < activeCells.length; i++) {
          const post = queue[activeCells[i]]
          if (post && (post.type === "video" || post.fileUrl?.match(/\.(mp4|webm|mov)$/i))) {
            videoCount++
          }
        }
        return videoCount > 0 && videoEndedCells.size >= videoCount
      },

      advanceAllCells: () => {
        const { queue, activeCells, shuffleQueue, loopQueue, repeatCurrentSet } = get()
        if (queue.length === 0 || repeatCurrentSet) return

        const cellCount = activeCells.length

        if (shuffleQueue) {
          const newCells = Array.from({ length: cellCount }, () => Math.floor(Math.random() * queue.length))
          set({ activeCells: newCells, videoEndedCells: new Set(), videoDurations: new Map(), cellSpeeds: new Map() })
          return
        }

        // Find the logical start of the current page so wrap-around cells (e.g. [8,9,0,1])
        // advance correctly instead of jumping back to page 0.
        const gridStart = findGridStart(activeCells, queue.length)
        const newStart = loopQueue
          ? (gridStart + cellCount) % queue.length
          : Math.min(gridStart + cellCount, queue.length - cellCount)
        const newCells = Array.from({ length: cellCount }, (_, i) => (newStart + i) % queue.length)

        set({
          activeCells: newCells,
          videoEndedCells: new Set(),
          videoDurations: new Map(),
          cellSpeeds: new Map(),
        })
      },

      getLayoutCellCount: () => {
        const { layout, customCellCount } = get()
        switch (layout) {
          case "single":
            return 1
          case "grid-2x2":
            return 4
          case "grid-2x3":
            return 6
          case "grid-2x4":
            return 8
          case "grid-custom":
            return customCellCount
          default:
            return 1
        }
      },

      initializeGridCells: () => {
        const { queue } = get()
        const cellCount = get().getLayoutCellCount()
        // One queue item per cell, in order — NOT `i % queue.length`. Wrapping
        // meant a grid with more cells than queue items (e.g. a 2x2 grid with
        // one pasted video) pointed every cell at the SAME queue index, so
        // "remove this cell" (which deletes that queue item, see the cell's
        // "Remove from queue" button in gooncave.tsx) wiped every cell that
        // happened to share it — reported as one delete clearing the whole
        // grid. Cells past the end of the queue get -1, the same "empty, show
        // Add content" sentinel already used when a preset drops a local file
        // (see lib/gooncave-presets.ts) — rendering already treats it that way.
        const cells: number[] = []
        for (let i = 0; i < cellCount; i++) {
          cells.push(i < queue.length ? i : -1)
        }
        set({ activeCells: cells, videoEndedCells: new Set(), videoDurations: new Map() })
      },

      advanceCell: (cellIndex) => {
        const { queue, activeCells, shuffleQueue, loopQueue } = get()
        if (queue.length === 0) return

        const newCells = [...activeCells]
        if (shuffleQueue) {
          newCells[cellIndex] = Math.floor(Math.random() * queue.length)
        } else {
          let nextIndex = newCells[cellIndex] + 1
          if (nextIndex >= queue.length) {
            nextIndex = loopQueue ? 0 : queue.length - 1
          }
          newCells[cellIndex] = nextIndex
        }
        set({ activeCells: newCells })
      },

      setCellSpeed: (cellIndex, speed) => {
        const { cellSpeeds } = get()
        const newSpeeds = new Map(cellSpeeds)
        newSpeeds.set(cellIndex, Math.max(0.25, Math.min(3, speed)))
        set({ cellSpeeds: newSpeeds })
      },

      resetCellSpeed: (cellIndex) => {
        const { cellSpeeds } = get()
        const newSpeeds = new Map(cellSpeeds)
        newSpeeds.delete(cellIndex)
        set({ cellSpeeds: newSpeeds })
      },

      setCellVolume: (cellIndex, volume) => {
        const { cellVolumes } = get()
        const newVolumes = new Map(cellVolumes)
        newVolumes.set(cellIndex, Math.max(0, Math.min(1, volume)))
        set({ cellVolumes: newVolumes })
      },

      toggleMuteCell: (cellIndex) => {
        const { mutedCells } = get()
        const newMuted = new Set(mutedCells)
        if (newMuted.has(cellIndex)) {
          newMuted.delete(cellIndex)
        } else {
          newMuted.add(cellIndex)
        }
        set({ mutedCells: newMuted })
      },

      muteAllCells: () => {
        set({ mutedCells: new Set([0, 1, 2, 3, 4, 5, 6, 7]) })
      },

      toggleLoopCell: (cellIndex) => {
        const { loopingCells } = get()
        const next = new Set(loopingCells)
        if (next.has(cellIndex)) {
          next.delete(cellIndex)
        } else {
          next.add(cellIndex)
        }
        set({ loopingCells: next })
      },

      unmuteAllCells: () => {
        set({ mutedCells: new Set() })
      },

      setAutoPlayVideos: (v) => set({ autoPlayVideos: v }),
      setAutoPlayGifs: (v) => set({ autoPlayGifs: v }),
    }),
    {
      name: "Gooncave-store",
      version: 1,
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as Record<string, unknown>
        if (version < 1) {
          // v0 defaulted autoLoadToQueue to true; force off for all existing users
          return { ...state, autoLoadToQueue: false }
        }
        return state
      },
      partialize: (state) => ({
        // Queue must survive a page reload too, not just in-app navigation —
        // it should only ever go away when the user explicitly clears it.
        queue: state.queue,
        currentIndex: state.currentIndex,
        layout: state.layout,
        customCellCount: state.customCellCount,
        customCols: state.customCols,
        slideshowInterval: state.slideshowInterval,
        shuffleQueue: state.shuffleQueue,
        loopQueue: state.loopQueue,
        expandAlbums: state.expandAlbums,
        source: state.source,
        autoLoadToQueue: state.autoLoadToQueue,
        gridDisplayMode: state.gridDisplayMode,
        repeatCurrentSet: state.repeatCurrentSet,
        autoPlayVideos: state.autoPlayVideos,
        autoPlayGifs: state.autoPlayGifs,
      }),
    },
  ),
)
